import React, {useMemo} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl} from 'react-intl';
import {ComboBox, FlexColumn, FlexRow, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {NovaModel, isNovaModelUnavailable} from '../shared/novaModelList';

interface Props {
	models: NovaModel[];
	// The agent's saved (or draft) model id. Null for an agent that has none yet.
	value: string | null;
	onChange: (modelId: string) => void;
	dataCy: string;
}

const MODEL_OPTION_MARKER = 'data-nova-model-option';

// Name on one line, guidance under it.
const ModelOption = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 6px 0;
	min-width: 0;
`;

// The design system's ComboBox option is a hard `height: 44px` with `padding: 16px`, leaving a 12px
// content box — one line, and a second overflows into the rows above and below. Its list is portalled
// to the document body, so it cannot be reached by a wrapper's own styles; this is a global rule
// instead, scoped by `:has()` to only those rows carrying the marker below, so no other ComboBox in
// the app is affected.
//
// The proper home for this is the design system: it already ships `Option.Label` / `Option.Description`
// with the ARIA wiring for a two-line option, and simply gives them no room. Fixing it there would let
// this override go.
const ModelOptionHeightStyle = createGlobalStyle`
	li:has([${MODEL_OPTION_MARKER}]) {
		height: auto;
		min-height: 44px;
	}
`;

// The engineering-owned model list, as a picker. Every entry here is one the service will accept:
// the endpoint withholds retired models and `mapNovaModelList` drops any that arrive anyway, so a
// row can never dead-end the agent it is chosen for. DEPRECATED is offered with a pill — it is
// still saveable, and pushing authors off it is the pill's job, not the filter's.
//
// A saved model that is no longer offered is shown and warned about, never silently replaced with
// the default: the model is part of the version content hash, so repairing the selection here would
// bake a model nobody chose into the next save. Saving is blocked instead — see the Save gate in
// NovaStudioShell, which asks `isNovaModelUnavailable` the same question this does.
export function NovaModelPicker({models, value, onChange, dataCy}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	// `isNovaModelUnavailable`, not a bare lookup: an empty list means the registry read failed, not
	// that every model was retired, and warning on that would tell the author their perfectly current
	// model was gone — on every agent — while offering nothing to replace it with.
	const isUnavailable = isNovaModelUnavailable(models, value);
	// Memoised on `models`: react-stately rebuilds the ComboBox collection whenever the array it is
	// handed changes identity rather than content, and this picker re-renders on every keystroke in
	// the prompt editors above it (they share the draft), so a fresh array each render would rebuild
	// the collection per character typed.
	const items = useMemo(() => models.map(model => ({id: model.modelId, name: model.displayName})), [models]);

	return (
		// `flex={0}`, because the design system's FlexColumn defaults to `flex: 1` — a growing item.
		// This picker is a form field: it should be as tall as its label and input, never a share of
		// whatever vertical space its parent happens to have. Inside the create-agent modal, whose
		// content column is height-capped and holds two other default-flex wrappers, that default
		// stretched the picker away from the fields around it; on the Prompt tab, where it is the only
		// such child of a content-sized column, the same markup looked right. Pinning the grow factor
		// makes it render identically wherever it is used.
		<FlexColumn gap="xs" flex={0}>
			<ModelOptionHeightStyle />
			<ComboBox
				data-cy={dataCy}
				label={formatMessage({id: 'nova.model.label'})}
				placeholder={formatMessage({id: 'nova.model.placeholder'})}
				// `defaultItems`, not `items`: useComboBoxState skips its own filtering entirely when
				// `items` is supplied ("No default filter if items are controlled"), so the box would
				// look searchable — placeholder and all — while typing narrowed nothing and the typed
				// text was reverted on blur. NovaTaskPicker passes `items` on purpose because its
				// matching is server-side; this list is short, local and fixed for the page's lifetime,
				// so the design system's own `contains` filter is exactly what it wants.
				defaultItems={items}
				width={ComboBox.WIDTH.FLEX}
				// `value`/`onChange`, not `selectedKey`/`onSelectionChange`: ComboBox destructures
				// `value` out of its props and assigns `ariaProps.selectedKey = value`
				// unconditionally, so anything passed as `selectedKey` is overwritten with undefined
				// and the box is left uncontrolled.
				value={isUnavailable ? null : value}
				onChange={(key: string | null) => {
					if (key) {
						onChange(key);
					}
				}}
			>
				{(item: {id: string; name: string}) => {
					const model = models.find(candidate => candidate.modelId === item.id);
					// Two lines: the model's name (with its status, when it has one) above its
					// guidance. The row's fixed height, which previously forced all three onto one
					// line, is relaxed by ModelOptionHeightStyle above.
					return (
						<ComboBox.Item key={item.id} textValue={item.name}>
							<ModelOption {...{[MODEL_OPTION_MARKER]: ''}}>
								<FlexRow gap="s" alignItems="center">
									<Text noWrap>{item.name}</Text>
									{model?.lifecycleState === 'DEPRECATED' ? (
										<Pill variant={PillVariant.NEUTRAL}>
											{formatMessage({id: 'nova.model.deprecated'})}
										</Pill>
									) : null}
								</FlexRow>
								{model?.hint ? (
									<Text color="auxiliary" size={2}>
										{model.hint}
									</Text>
								) : null}
							</ModelOption>
						</ComboBox.Item>
					);
				}}
			</ComboBox>
			{isUnavailable ? (
				<div data-cy={`${dataCy}-unavailable`} data-testid={`${dataCy}-unavailable`}>
					<FlexColumn gap="xxs" alignItems="flex-start">
						<Text>{formatMessage({id: 'nova.model.unavailableOption'}, {modelId: value})}</Text>
						<Text color="error">{formatMessage({id: 'nova.model.unavailableWarning'})}</Text>
					</FlexColumn>
				</div>
			) : null}
		</FlexColumn>
	);
}

export default NovaModelPicker;
